import { useState } from 'react';
import { SignInMethodOption, SignInVerification } from '../../../entities/SignInMethod';
import { LoginStep } from '../../../entities/Login';
import LoginModel from './model';

type LoginViewModelProps = {
  onContinueWithoutLogin?: () => void | Promise<void>;
  onSuccess?: (token: string) => void | Promise<void>;
};

export default function LoginViewModel({ onContinueWithoutLogin, onSuccess }: LoginViewModelProps) {
  const model = LoginModel;
  const [activeStep, setActiveStep] = useState<LoginStep>(LoginStep.CPF);
  const [verifyData, setVerifyData] = useState<SignInVerification | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<SignInMethodOption | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const goToNextStep = (current: LoginStep) => {
    setIsLoading(false);
    setActiveStep(model.nextStep(current));
  };

  const handleUserVerified = (data: SignInVerification) => {
    setVerifyData(data);
    goToNextStep(LoginStep.CPF);
  };

  const handleMethodSelected = (method: SignInMethodOption) => {
    setSelectedMethod(method);
    goToNextStep(LoginStep.CODE_METHOD);
  };

  const handleMethodChanged = (method: SignInMethodOption) => {
    setSelectedMethod(method);
  };

  const handleVerified = (token: string) => {
    goToNextStep(LoginStep.CODE);
    onSuccess?.(token);
  };

  const handleLoadingChange = (loading: boolean) => {
    setIsLoading(loading);
  };

  const handleContinueWithoutLogin = async () => {
    if (isDisabled) return;
    setIsDisabled(true);
    setIsLoading(true);
    try {
      await onContinueWithoutLogin?.();
    } finally {
      setIsLoading(false);
    }
  };

  return {
    activeStepIndex: model.getStepIndex(activeStep),
    steps: model.steps,
    isCpfStep: activeStep === LoginStep.CPF,
    isCodeMethodStep: activeStep === LoginStep.CODE_METHOD,
    isCodeStep: activeStep === LoginStep.CODE,
    isSuccessStep: activeStep === LoginStep.SUCCESS,
    isLoading,
    isDisabled,
    verifyData,
    selectedMethod,
    handleUserVerified,
    handleMethodSelected,
    handleMethodChanged,
    handleVerified,
    handleLoadingChange,
    handleContinueWithoutLogin
  };
}
